import { useCallback, useContext, useRef, useSyncExternalStore } from 'react';
import { useIsomorphicLayoutEffect } from '@direct-frontend/stdlib';
import { useAutomaticPresentationCoordinator } from './AutomaticPresentationProvider';
import { AutomaticPresentationRouteGenerationContext } from './AutomaticPresentationRouteBoundary';
import {
    type AutomaticPresentationCloseReason,
    type AutomaticPresentationConditions,
    type AutomaticPresentationOptions,
    type AutomaticPresentationRegistration,
} from './types';

type AutomaticPresentationState = 'inactive' | 'active' | 'leaving';

/** Управление отображением одной React-поверхности, зарегистрированной в общей очереди. */
export type AutomaticPresentationControl = Readonly<{
    /** Удобная форма состояния для передачи в `open` или аналогичный prop компонента. */
    isActive: boolean;
    /** Помогает оставить компонент смонтированным, пока завершается анимация закрытия. */
    isLeaving: boolean;
    /** Начинает закрытие активной поверхности и сообщает coordinator результат показа. */
    startLeaving: (reason: AutomaticPresentationCloseReason) => boolean;
    /** Освобождает общий слот после фактического завершения анимации закрытия. */
    completeLeaving: () => boolean;
}>;

const getServerState = (): AutomaticPresentationState => 'inactive';

/**
 * Регистрирует автоматический показ на время жизни компонента и возвращает разрешение на его отображение.
 * `eligibility` и `isReady` обновляют текущую регистрацию; изменение остальных параметров создаёт новую.
 *
 * @kind helper
 * @returns Состояние разрешения и методы, связывающие закрытие UI с освобождением общего слота.
 */
export function useAutomaticPresentation(options: AutomaticPresentationOptions): AutomaticPresentationControl {
    const coordinator = useAutomaticPresentationCoordinator();
    const routeGeneration = useContext(AutomaticPresentationRouteGenerationContext);
    const registrationRef = useRef<AutomaticPresentationRegistration | null>(null);
    const latestConditionsRef = useRef<AutomaticPresentationConditions>({
        eligibility: options.eligibility,
        isReady: options.isReady,
    });
    const debugLabel = options.debug?.label;
    const debugOwner = options.debug?.owner;
    const debugSurface = options.debug?.surface;
    // Компонент может сохраниться между маршрутами, поэтому новое поколение обновляет его route-регистрацию.
    const registrationRouteGeneration = options.scope === 'application' ? null : routeGeneration;

    useIsomorphicLayoutEffect(() => {
        const conditions: AutomaticPresentationConditions = {
            eligibility: options.eligibility,
            isReady: options.isReady,
        };

        // Обычное обновление данных не должно создавать новую регистрацию и заново запускать её ожидание.
        latestConditionsRef.current = conditions;
        registrationRef.current?.update(conditions);
    }, [options.eligibility, options.isReady]);

    useIsomorphicLayoutEffect(() => {
        const registration = coordinator.register({
            id: options.id,
            priority: options.priority,
            order: options.order,
            scope: options.scope,
            debug:
                debugLabel === undefined && debugOwner === undefined && debugSurface === undefined
                    ? undefined
                    : {
                          label: debugLabel,
                          owner: debugOwner,
                          surface: debugSurface,
                      },
            ...latestConditionsRef.current,
        });

        registrationRef.current = registration;

        return () => {
            registration.unregister();

            if (registrationRef.current === registration) {
                registrationRef.current = null;
            }
        };
    }, [
        coordinator,
        debugLabel,
        debugOwner,
        debugSurface,
        options.id,
        options.order,
        options.priority,
        options.scope,
        registrationRouteGeneration,
    ]);

    const getRegistrationState = useCallback((): AutomaticPresentationState => {
        if (registrationRef.current?.isActive()) {
            return 'active';
        }

        if (registrationRef.current?.isLeaving()) {
            return 'leaving';
        }

        return 'inactive';
    }, []);
    const state = useSyncExternalStore(coordinator.subscribe, getRegistrationState, getServerState);
    const startLeaving = useCallback(
        (reason: AutomaticPresentationCloseReason) => registrationRef.current?.startLeaving(reason) ?? false,
        []
    );
    const completeLeaving = useCallback(() => registrationRef.current?.completeLeaving() ?? false, []);

    return {
        isActive: state === 'active',
        isLeaving: state === 'leaving',
        startLeaving,
        completeLeaving,
    };
}
